import { badgeVariants } from '@heidi/design-system/badge';
import {
  CitationPopover,
  CitationPopoverContent,
  CitationPopoverTrigger,
} from '@heidi/design-system/citation-popover';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { cn, getDomainFaviconUrl, getFaviconUrl, useIsMobile } from '@heidi/utils';
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
} from 'react';
import type { EvidenceSurfaceBlock, SimpleCitation } from '../../contracts';
import {
  buildViewerSource,
  getCitationKey,
  getDisplayName,
  getDomainFromUrl,
  isFileCitation,
  isPartnerCitation,
} from '../../contracts';
import { hasLocalRetrieval } from '../../contracts/retrievedFrom';
import {
  trackEvidenceAnswerEvent,
  trackEvidenceSourceClick,
} from '../../data/analytics/answer';
import { PartnerLogo } from './PartnerLogo';
import { useEvidenceAnswerPorts } from './ports';
import { SourceCard, type SourceCardVariant } from './SourceCard';
import { SourceCardV3Popover } from './SourceCardV3Popover';
import { getFileSourceIcon } from './sourceIconUtils';
import { shouldOpenSourceInViewer } from './sourceViewerRouting';

/**
 * CitationSource is an alias for SimpleCitation.
 * Kept for backwards compatibility with existing imports.
 */
export type CitationSource = SimpleCitation;

interface CitationBadgeProps {
  sources: CitationSource[];
  className?: string;
  /** Visual variant. "v3" (default) uses stepper navigation and redesigned SourceCard layout. */
  variant?: SourceCardVariant;
  /** Backend chat ID for analytics correlation (EVI-1486). */
  chatId?: string;
  /** Backend message ID (msg_*) for analytics correlation (EVI-1486). */
  messageId?: string;
  /** Scribe session ID for analytics correlation (EVI-1486). */
  sessionId?: string;
  /**
   * Containing block where this badge is rendered — `'prose'` for inline
   * paragraphs and `'key_points'` for the blue Key Points card. Lets
   * dashboards distinguish "summary scan" from "deep-read" clicks.
   * EVI-1486 round 4 / round 7 plumbing.
   */
  surfaceBlock?: EvidenceSurfaceBlock;
  /**
   * 1-based ordinal of this badge within its surface_block (i.e. the
   * cite-badge `data-idx + 1` for prose / key_points). Combined with
   * `message_id` and `surface_block` it uniquely identifies which badge
   * was clicked even when the same URL appears multiple times. EVI-1486
   * round 4.
   */
  citationNumber?: number;
  /**
   * Skip semi-transparent partner brand fills so badges look the same as in
   * prose when rendered on a tinted surface (e.g. Key Safety Notes).
   */
  opaquePartnerFill?: boolean;
  /**
   * Visual theme for badges rendered against tinted surfaces. `'default'`
   * (neutral chrome) is correct for prose. `'key-points'` swaps to
   * info-toned semantic tokens so the badge stays legible on the blue
   * `KeyPointsBlock` card without disappearing into it. Encoded here rather
   * than via a long `className` override soup at the call site so callers
   * can't accidentally drift apart.
   */
  surfaceTheme?: 'default' | 'key-points';
  /** Evidence run that owns this badge, so a Work citations row can find it. */
  runId?: string;
}

/**
 * CitationBadge - Hoverable badge that shows source details in a hover card
 *
 * Displays abbreviated source name with count (e.g., "Pediatrics +1")
 * On hover, shows hover card with full source list
 *
 * Memoized to prevent re-renders during streaming that would cause
 * the hover card to flicker/close unexpectedly.
 */
/** Delay before opening popover to prevent accidental triggers */
const OPEN_DELAY_MS = 200;
/** Time allowed to move mouse from badge to popover content */
const CLOSE_DELAY_MS = 200;
/** How long to keep popover open when triggered programmatically */
const PROGRAMMATIC_OPEN_DURATION_MS = 3000;
/** Evidence's references accordion uses this event to open a citation popover. */
export const CITATION_OPEN_EVENT = 'open-citation-popover';
const HIGHLIGHT_DURATION_MS = 2000;
/** Work's citations list marks the badge instead of opening the hover card. */
export const CITATION_HIGHLIGHT_EVENT = 'highlight-citation-badge';
/** Maximum length for file name abbreviation in badge */
const FILE_NAME_MAX_LENGTH = 15;

/**
 * Abbreviates a file name for display in citation badge
 * Examples:
 * - "report.pdf" → "report.pdf" (fits within limit)
 * - "medical-guidelines-2024.docx" → "medical-guid..." (truncated)
 */
function abbreviateFileName(fileName: string): string {
  if (fileName.length <= FILE_NAME_MAX_LENGTH) {
    return fileName;
  }
  return fileName.slice(0, FILE_NAME_MAX_LENGTH) + '...';
}

/** Opens a web source in the viewer panel (desktop) or new tab (mobile) */
const SourceViewerLink = ({
  source,
  className,
  onClick,
  children,
}: {
  source: CitationSource;
  className?: string;
  onClick?: () => void;
  children: React.ReactNode;
}) => {
  const { openInViewer, openInNewTab, isAuthenticatedUrl } = useEvidenceAnswerPorts();
  const isMobile = useIsMobile();

  const handleClick = () => {
    onClick?.();
    if (source.url) {
      if (
        shouldOpenSourceInViewer({
          url: source.url,
          embed: source.embed,
          isMobile,
          isAuthenticatedUrl,
        })
      ) {
        openInViewer(buildViewerSource(source, getDomainFaviconUrl));
      } else {
        openInNewTab(source.url);
      }
    }
  };

  return (
    <button
      type="button"
      className={cn(className, 'w-full text-left')}
      onClick={handleClick}
    >
      {children}
    </button>
  );
};

const CitationBadgeInner = ({
  sources,
  className,
  variant = 'v3',
  chatId,
  messageId,
  sessionId,
  surfaceBlock,
  citationNumber,
  opaquePartnerFill = false,
  surfaceTheme = 'default',
  runId,
}: CitationBadgeProps): JSX.Element | null => {
  const {
    openInViewer,
    openLibraryDocument,
    openInNewTab,
    isPublicRoute: isPublicRoutePort,
    isAuthenticatedUrl,
    getPartnerConfig,
    analyticsBaseProps,
  } = useEvidenceAnswerPorts();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [isBadgeHovered, setIsBadgeHovered] = useState(false); // EVI-590: partner badge hover
  const [isHighlighted, setIsHighlighted] = useState(false);
  const isHoveringRef = useRef(false);
  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLButtonElement & HTMLSpanElement>(null);
  const autoCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isV3 = variant === 'v3';

  const isPublicRoute = isPublicRoutePort();

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      if (autoCloseTimeoutRef.current) clearTimeout(autoCloseTimeoutRef.current);
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    };
  }, []);

  // A reference scroll either opens the popover (Evidence) or marks the badge (Work).
  useEffect(() => {
    const element = triggerRef.current;
    if (!element) return;

    const handleOpenEvent = () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }

      setIsOpen(true);

      // Auto-close after duration (unless user hovers)
      autoCloseTimeoutRef.current = setTimeout(() => {
        if (!isHoveringRef.current) {
          setIsOpen(false);
        }
      }, PROGRAMMATIC_OPEN_DURATION_MS);
    };

    const handleHighlightEvent = () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
        highlightTimeoutRef.current = null;
      }

      setIsHighlighted(true);
      highlightTimeoutRef.current = setTimeout(() => {
        setIsHighlighted(false);
      }, HIGHLIGHT_DURATION_MS);
    };

    element.addEventListener(CITATION_OPEN_EVENT, handleOpenEvent);
    element.addEventListener(CITATION_HIGHLIGHT_EVENT, handleHighlightEvent);
    return () => {
      element.removeEventListener(CITATION_OPEN_EVENT, handleOpenEvent);
      element.removeEventListener(CITATION_HIGHLIGHT_EVENT, handleHighlightEvent);
    };
  }, []);

  const handleSourceClick = useCallback(
    (
      source: CitationSource,
      interaction: 'open_in_viewer' | 'open_in_new_tab' | 'mobile_tap' = 'open_in_viewer'
    ) => {
      if (!source.url) return; // Only track web sources with URLs
      const domain = getDomainFromUrl(source.url);
      // EVI-1486: unified `evidence_source_clicked` shape — adds
      // `surface`, `interaction`, `chat_id`, `message_id`, `session_id`,
      // `source_type` so dashboards can answer "who clicked which source
      // from which surface" without joining multiple events.
      trackEvidenceSourceClick({
        source_url: source.url,
        source_domain: domain,
        source_title: source.title || undefined,
        source_type: source.source_type,
        // EVI-1486 round 7 + EVI-1592: library collections (array; undefined for web sources).
        collections: source.collections?.length ? source.collections : undefined,
        surface: 'inline_citation_badge',
        // EVI-1486 round 4 / round 7: containing block + 1-based badge
        // ordinal so dashboards can distinguish "summary scan" (key_points)
        // from "deep-read" (prose) clicks and disambiguate duplicate URLs.
        surface_block: surfaceBlock,
        citation_number: citationNumber,
        interaction,
        chat_id: chatId,
        message_id: messageId,
        session_id: sessionId,
      });
      // Generic object_action event — kept in lockstep with the unified
      // event so the existing button-clicked dashboard keeps working.
      trackEvidenceAnswerEvent(
        {
          type: 'source_selected',
          source_url: source.url,
          source_domain: domain,
          source_title: source.title || undefined,
        },
        analyticsBaseProps
      );
    },
    [chatId, messageId, sessionId, surfaceBlock, citationNumber, analyticsBaseProps]
  );

  /**
   * EVI-1486: track an open of an attached library/user-document citation.
   *
   * Previously these three paths (mobile tap, V3 popover row, legacy popover
   * row) opened the in-app document preview without firing any analytics, so
   * the only observable signal was the citation badge appearing. Now we emit
   * `evidence_source_clicked` with `interaction: 'open_library_document'`
   * and `source_type: 'user_document'` so dashboards can quantify library
   * usage end-to-end.
   */
  const handleLibraryDocumentOpen = useCallback(
    (source: CitationSource) => {
      trackEvidenceSourceClick({
        // Library docs have no URL — emit the citation key for joinability.
        source_url: source.url || '',
        source_domain: source.url ? getDomainFromUrl(source.url) : '',
        source_title: source.title || undefined,
        source_type: source.source_type,
        // EVI-1486 round 7 + EVI-1592: `collections` carries the library buckets.
        collections: source.collections?.length ? source.collections : undefined,
        surface: 'inline_citation_badge',
        surface_block: surfaceBlock,
        citation_number: citationNumber,
        interaction: 'open_library_document',
        chat_id: chatId,
        message_id: messageId,
        session_id: sessionId,
      });
    },
    [chatId, messageId, sessionId, surfaceBlock, citationNumber]
  );

  /** On mobile, tap opens the source directly in a new tab (no popover) */
  const handleMobileTap = useCallback(() => {
    const primarySource = sources[0];
    if (!primarySource) return;

    const isUserDocument = primarySource.source_type === 'user_document';
    const userDocumentId = isUserDocument
      ? primarySource.source_id || primarySource.document_id
      : undefined;

    if (isUserDocument && userDocumentId) {
      handleLibraryDocumentOpen(primarySource);
      openLibraryDocument(userDocumentId);
      return;
    }

    if (primarySource.url) {
      handleSourceClick(primarySource, 'mobile_tap');
      if (
        shouldOpenSourceInViewer({
          url: primarySource.url,
          embed: primarySource.embed,
          isMobile,
          isAuthenticatedUrl,
        })
      ) {
        openInViewer(buildViewerSource(primarySource, getDomainFaviconUrl));
      } else {
        openInNewTab(primarySource.url);
      }
    }
  }, [
    sources,
    handleSourceClick,
    handleLibraryDocumentOpen,
    openLibraryDocument,
    openInViewer,
    openInNewTab,
    isAuthenticatedUrl,
    isMobile,
  ]);

  /**
   * Custom open change handler that respects our hover tracking.
   * Only closes when mouse is truly not hovering.
   * This prevents flickering during streaming when DOM re-renders.
   */
  const handleOpenChange = useCallback((open: boolean) => {
    if (open) {
      // Clear any pending close when opening
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setIsOpen(true);
    } else if (!isHoveringRef.current) {
      // Only close if we're not hovering
      setIsOpen(false);
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    // Cancel any pending close
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    isHoveringRef.current = true;
    setIsBadgeHovered(true);

    // Delay open to prevent accidental triggers
    openTimeoutRef.current = setTimeout(() => {
      if (isHoveringRef.current) {
        setIsOpen(true);
      }
    }, OPEN_DELAY_MS);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Cancel any pending open
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    isHoveringRef.current = false;
    setIsBadgeHovered(false);

    // Delay closing to allow mouse to move from badge to popover content
    closeTimeoutRef.current = setTimeout(() => {
      if (!isHoveringRef.current) {
        setIsOpen(false);
      }
    }, CLOSE_DELAY_MS);
  }, []);

  if (!sources.length) return null;

  const primarySource = sources[0];
  if (!primarySource) return null;
  const isFileSource = isFileCitation(primarySource.source_type);
  // MOD-1726: mixed-ness is judged over the sources this badge cites, which is
  // the list its popover shows; the References list judges the whole answer.
  const hasLocalSources = hasLocalRetrieval(sources);
  // Library sources (user_document) get distinct styling
  const isLibrarySource = primarySource.source_type === 'user_document';
  // EVI-678: Integration partner sources get custom branding
  const isPartnerSource = isPartnerCitation(primarySource.source_type);
  const basePartnerConfig =
    isPartnerSource && primarySource.partner_type
      ? getPartnerConfig(primarySource.partner_type)
      : undefined;
  // EVI-1105: Prefer per-citation branding (zone-specific HealthPathways logo /
  // colour) over the partner-level catalog values, falling back when absent.
  // `nameInLogo` (wordmark flag) is preserved via the spread — it stays
  // partner-keyed, not per-citation.
  const partnerConfig = basePartnerConfig
    ? {
        ...basePartnerConfig,
        logo: primarySource.logo_url ?? basePartnerConfig.logo,
        brandColor: primarySource.brand_color ?? basePartnerConfig.brandColor,
      }
    : undefined;

  // For file-based sources, prefer title (formatted) over file_name (raw filename)
  // For partner sources, use display_name from config or backend
  const abbreviation = isFileSource
    ? abbreviateFileName(primarySource.title || primarySource.file_name || '')
    : isPartnerSource && partnerConfig
      ? partnerConfig.displayName
      : getDisplayName(primarySource);

  const additionalCount = sources.length - 1;

  // Get icon for file-based sources
  const FileIcon = getFileSourceIcon(
    primarySource.source_type,
    primarySource.file_name || primarySource.title
  );

  // Get citation identifier for scroll-to-citation feature
  const citationIdentifier = getCitationKey(primarySource);

  // EVI-678: Custom styling for partner citations (badge and popover)
  // EVI-590: Use opacity change on hover (same pattern as ReferenceCard)
  // Default: 10 (~6% opacity), Hover: 1A (~10% opacity)
  const partnerBadgeStyle: CSSProperties | undefined = partnerConfig
    ? {
        borderColor: partnerConfig.brandColor,
        borderWidth: '1px',
        ...(opaquePartnerFill
          ? {}
          : {
              backgroundColor: isBadgeHovered
                ? `${partnerConfig.brandColor}1A`
                : `${partnerConfig.brandColor}10`,
            }),
      }
    : undefined;

  // EVI-678: Partner popover gets solid background with subtle tint over the
  // themed surface token (so it respects light/dark mode). Fallback `0 0% 100%`
  // (white HSL components) keeps `hsl()` valid if no theme class is applied,
  // e.g. SSR before hydration.
  const partnerPopoverStyle: CSSProperties | undefined = partnerConfig
    ? {
        borderColor: partnerConfig.brandColor,
        borderWidth: '1px',
        background: `linear-gradient(${partnerConfig.brandColor}0D, ${partnerConfig.brandColor}0D), hsl(var(--twc-surface, 0 0% 100%))`,
      }
    : undefined;

  // Surface-theme tints come AFTER the default chrome in the cn() arg list
  // so twMerge drops the conflicting bg / border / text utilities. This is
  // why we don't need `!important` here even though the badge variant + the
  // default background/border/text classes nominally collide with the theme.
  const surfaceThemeClass =
    surfaceTheme === 'key-points'
      ? 'border-validation-info-border bg-validation-info-muted text-text-secondary hover:border-validation-info hover:bg-validation-info-border [&>span]:text-text-secondary'
      : null;

  const badgeClassName = cn(
    badgeVariants({ color: 'neutral', size: 'sm' }),
    'ml-0.5 cursor-pointer gap-1 border bg-background-tertiary shadow-none transition-colors',
    isPartnerSource ? 'border-current' : 'border-transparent',
    isPartnerSource
      ? 'text-text-secondary'
      : isLibrarySource
        ? 'bg-violet-50 text-violet-700 hover:bg-violet-100 dark:bg-violet-900/30 dark:text-violet-300 dark:hover:bg-violet-900/40'
        : 'text-text-secondary hover:border-border-strong-hover hover:bg-background-tertiary-hover',
    surfaceThemeClass,
    isHighlighted && 'bg-surface-selected ring-2 ring-surface-selected',
    className
  );

  // EVI-1105: suppress the sibling name only when rendering a per-citation zone
  // logo — those assets (HealthPathways CHP/HHP) are wordmarks that already
  // contain the partner name. The neutral partner-level logo keeps its label.
  const hidePartnerName = !!primarySource.logo_url;

  const badgeContent = (
    <>
      {isFileSource && FileIcon && <FileIcon size={14} className="shrink-0" />}
      {isPartnerSource && (
        <PartnerLogo
          src={partnerConfig?.logo}
          brandColor={partnerConfig?.brandColor}
          size={hidePartnerName ? 16 : 14}
          alt={partnerConfig?.displayName}
          sourceUrl={primarySource.url}
          wordmark={hidePartnerName}
        />
      )}
      {!isFileSource && !isPartnerSource && primarySource.url && (
        // eslint-disable-next-line @next/next/no-img-element -- External dynamic favicons
        <img
          src={getFaviconUrl(primarySource.url)}
          alt=""
          className="size-3.5 shrink-0 rounded-sm"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
      {!hidePartnerName && <span className="min-w-0 truncate">{abbreviation}</span>}
      {additionalCount > 0 && (
        <span
          className={
            isLibrarySource
              ? 'text-violet-500'
              : isPartnerSource && partnerConfig
                ? 'text-text-secondary'
                : 'text-text-tertiary'
          }
        >
          +{additionalCount}
        </span>
      )}
    </>
  );

  if (isMobile) {
    return (
      <button
        type="button"
        ref={triggerRef}
        data-citation-url={citationIdentifier}
        data-citation-run={runId}
        data-testid="citation-badge"
        onClick={handleMobileTap}
        style={partnerBadgeStyle}
        className={badgeClassName}
      >
        {badgeContent}
      </button>
    );
  }

  return (
    <CitationPopover open={isOpen} onOpenChange={handleOpenChange}>
      <CitationPopoverTrigger asChild>
        <span
          ref={triggerRef}
          data-citation-url={citationIdentifier}
          data-citation-run={runId}
          data-testid="citation-badge"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={partnerBadgeStyle}
          className={badgeClassName}
        >
          {badgeContent}
        </span>
      </CitationPopoverTrigger>

      <CitationPopoverContent
        side="top"
        align="start"
        sideOffset={4}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={partnerPopoverStyle}
      >
        {isV3 ? (
          <SourceCardV3Popover
            sources={sources}
            hasLocalSources={hasLocalSources}
            showSaveAction={!isPublicRoute}
            onSourceSelect={(source) => {
              const isUserDocument = source.source_type === 'user_document';
              const userDocumentId = isUserDocument
                ? source.source_id || source.document_id
                : undefined;
              if (isUserDocument && userDocumentId) {
                handleLibraryDocumentOpen(source);
                openLibraryDocument(userDocumentId);
              } else {
                handleSourceClick(source, isMobile ? 'mobile_tap' : 'open_in_viewer');
                if (source.url) {
                  if (
                    shouldOpenSourceInViewer({
                      url: source.url,
                      embed: source.embed,
                      isMobile,
                      isAuthenticatedUrl,
                    })
                  ) {
                    openInViewer(buildViewerSource(source, getDomainFaviconUrl));
                  } else {
                    openInNewTab(source.url);
                  }
                }
              }
              setIsOpen(false);
            }}
          />
        ) : (
          <ScrollArea
            type="auto"
            className="max-h-80 min-w-0"
            viewportClassName="max-h-80"
          >
            <div className="flex min-w-0 flex-col">
              {sources.map((source, idx) => {
                const isFirst = idx === 0;
                const isLast = idx === sources.length - 1;
                const isSourceFile = isFileCitation(source.source_type);
                const isSourceLibrary = source.source_type === 'user_document';
                const canSave =
                  !isSourceFile && !isSourceLibrary && !!source.url && !isPublicRoute;

                const containerClassName = cn(
                  'group block min-w-0 px-4 py-2.5',
                  isFirst && 'rounded-t-md pt-4',
                  isLast && 'rounded-b-md pb-4',
                  isFirst && isLast && 'rounded-md'
                );

                const isUserDocument = source.source_type === 'user_document';
                const userDocumentId = isUserDocument
                  ? source.source_id || source.document_id
                  : undefined;

                if (isUserDocument && userDocumentId) {
                  return (
                    <button
                      key={`${getCitationKey(source)}-${idx}`}
                      type="button"
                      className={cn(containerClassName, 'w-full text-left')}
                      onClick={() => {
                        handleLibraryDocumentOpen(source);
                        openLibraryDocument(userDocumentId);
                        setIsOpen(false);
                      }}
                    >
                      <SourceCard
                        {...source}
                        sourceIndex={idx}
                        chatId={chatId}
                        sessionId={sessionId}
                        messageId={messageId}
                        hasLocalSources={hasLocalSources}
                      />
                    </button>
                  );
                }

                const titleClickHandler = !isSourceFile
                  ? () => handleSourceClick(source)
                  : undefined;

                return (
                  <SourceViewerLink
                    key={`${getCitationKey(source)}-${idx}`}
                    source={source}
                    className={containerClassName}
                    onClick={() => {
                      handleSourceClick(
                        source,
                        isMobile ? 'mobile_tap' : 'open_in_viewer'
                      );
                      setIsOpen(false);
                    }}
                  >
                    <SourceCard
                      {...source}
                      sourceIndex={idx}
                      chatId={chatId}
                      sessionId={sessionId}
                      messageId={messageId}
                      onTitleClick={titleClickHandler}
                      showSaveAction={canSave}
                      hasLocalSources={hasLocalSources}
                    />
                  </SourceViewerLink>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CitationPopoverContent>
    </CitationPopover>
  );
};

/**
 * Memoized CitationBadge - prevents re-renders during streaming.
 * Uses shallow comparison of sources array by checking URLs.
 */
export const CitationBadge = memo(CitationBadgeInner, (prevProps, nextProps) => {
  if (prevProps.className !== nextProps.className) return false;
  if (prevProps.variant !== nextProps.variant) return false;
  if (prevProps.sources.length !== nextProps.sources.length) return false;
  // EVI-1486: chat/message/session ids + surfaceBlock + citationNumber all
  // feed analytics — re-render if any changes so click handlers close over
  // the latest values. In practice these are stable per badge (derived from
  // position in the response) so this is a no-op during streaming.
  if (prevProps.chatId !== nextProps.chatId) return false;
  if (prevProps.messageId !== nextProps.messageId) return false;
  if (prevProps.sessionId !== nextProps.sessionId) return false;
  if (prevProps.surfaceBlock !== nextProps.surfaceBlock) return false;
  if (prevProps.citationNumber !== nextProps.citationNumber) return false;
  if (prevProps.opaquePartnerFill !== nextProps.opaquePartnerFill) return false;
  if (prevProps.surfaceTheme !== nextProps.surfaceTheme) return false;
  if (prevProps.runId !== nextProps.runId) return false;

  return prevProps.sources.every(
    (source, idx) => source.url === nextProps.sources[idx]?.url
  );
});
